import { useState, useRef, useEffect } from 'react';
import type { ReactNode } from 'react';
import { ChevronRight, LogOut } from 'lucide-react';
import { NavItem } from './NavItem';
import { Button } from './Button';
import type { NavItemData } from './NavItem';
import sidenavToggleIcon from '../../assets/icons/sidenav-toggle.svg';
import nyaiSidenavMark from '../../assets/icons/nyai-sidenav-mark.svg';

const cn = (...classes: (string | false | undefined)[]) => classes.filter(Boolean).join(' ');

const Tip = ({ label, children }: { label: ReactNode; children: ReactNode }) => (
  <div className="relative group/tip">
    {children}
    <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-[#1a1f2b] text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/tip:opacity-100 pointer-events-none z-50 transition-opacity duration-150 shadow-lg border border-white/10">
      {label}
    </div>
  </div>
);

const DockIcon = ({ flipped = false }: { flipped?: boolean }) => (
  <img
    src={sidenavToggleIcon}
    alt=""
    width={20}
    height={20}
    className={flipped ? 'rotate-180' : ''}
  />
);

export type NavGroup = NavItemData[];

export interface SideNavProps {
  items: NavGroup[];
  // The current route path, used to resolve each item's active state. The
  // real app would pass useLocation().pathname here — SideNav itself doesn't
  // call useLocation() so it stays easy to preview with a fixed value.
  activePath: string;
  brandName?: string;
  // Overrides the brand header's expanded-state content (default: the
  // brandName text label) — e.g. a logo mark, when a caller wants one
  // instead of text. Ignored while collapsed, which always shows DockIcon.
  brandLogo?: ReactNode;
  // Optional content rendered between the nav items and the profile
  // footer (e.g. a "Logged in to {tenant}" + switch control). The caller
  // is responsible for adapting its own content to the collapsed state,
  // since it already controls `collapsed` when used in controlled mode.
  footerSlot?: ReactNode;
  userName?: string;
  onLogout?: () => void;
  // Controls the collapsed/expanded width. Uncontrolled (internal state,
  // defaults to expanded) when omitted — pass both to control it externally.
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

// Collapsible sidebar navigation — extracted from the real app's
// layout/Sidebar.jsx into the component library. Behavior match includes:
// brand header with collapse/expand toggle, grouped nav items (with
// tooltips when collapsed), and a profile footer with a logout menu.
// Auto-collapses below a 900px viewport width the same way the original did.
export const SideNav = ({
  items,
  activePath,
  brandName = 'NyAI',
  brandLogo,
  footerSlot,
  userName = 'User',
  onLogout,
  collapsed: collapsedProp,
  onCollapsedChange,
}: SideNavProps) => {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const isCollapsed = collapsedProp ?? internalCollapsed;
  const setIsCollapsed = (value: boolean) => {
    setInternalCollapsed(value);
    onCollapsedChange?.(value);
  };
  const [profileOpen, setProfileOpen] = useState(false);

  const lastWidth = useRef(window.innerWidth);
  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      if (w < 900 && lastWidth.current >= 900) setIsCollapsed(true);
      else if (w >= 900 && lastWidth.current < 900) setIsCollapsed(false);
      lastWidth.current = w;
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const isItemActive = (item: NavItemData) =>
    activePath === item.path || (item.path !== '/' && activePath.startsWith(item.path + '/'));

  return (
    <aside
      className={cn(
        'flex flex-col h-full transition-all duration-300 ease-in-out shrink-0 relative z-40',
        'bg-[#031C30] text-gray-300',
        isCollapsed ? 'w-[72px]' : 'w-[230px]',
      )}
    >
      {/* Brand header */}
      <div
        className={cn(
          'flex items-center transition-all duration-300 shrink-0',
          isCollapsed
            ? 'bg-[linear-gradient(112.04deg,#3F707D_0%,#031C30_40%)] justify-center h-[60px] p-0'
            : 'bg-[linear-gradient(112.04deg,#3F707D_0%,#031C30_20%)] ps-3 pe-1 justify-between h-[60px]',
        )}
      >
        {isCollapsed ? (
          <Button
            variant="tertiary"
            onClick={() => setIsCollapsed(false)}
            className="relative group w-full h-full !p-0 !rounded-none hover:!bg-transparent"
            title="Expand Sidebar"
          >
            <img
              src={nyaiSidenavMark}
              alt=""
              width={32}
              height={24}
              className="transition-opacity duration-200 group-hover:opacity-0"
            />
            <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <DockIcon flipped />
            </span>
          </Button>
        ) : (
          <>
            {brandLogo ?? (
              <span className={`text-white font-semibold text-lg tracking-wide ${brandName.length > 14 ? 'text-base' : ''}`}>
                {brandName}
              </span>
            )}
            <Button
              variant="tertiary"
              onClick={() => setIsCollapsed(true)}
              className="!text-white hover:!text-white/70 hover:!bg-transparent !p-1 shrink-0"
              title="Collapse Sidebar"
            >
              <DockIcon />
            </Button>
          </>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-2 overflow-y-auto overflow-x-hidden">
        {items.map((group, gi) => (
          <div key={gi}>
            {gi > 0 && <div className="mx-2 my-2 border-t border-white/10" />}
            <div className={cn('space-y-1 w-full', isCollapsed && 'flex flex-col')}>
              {group.map((item) => {
                const navItemEl = <NavItem item={item} active={isItemActive(item)} collapsed={isCollapsed} />;
                if (isCollapsed) {
                  return <Tip key={item.path} label={item.label}>{navItemEl}</Tip>;
                }
                return <div key={item.path} className="px-2 w-full">{navItemEl}</div>;
              })}
            </div>
          </div>
        ))}
      </nav>

      {footerSlot && (
        <div className="border-t border-white/10 shrink-0">{footerSlot}</div>
      )}

      {/* Profile footer */}
      <div className={cn('p-2 border-t border-white/10 transition-all duration-300 relative', isCollapsed ? 'flex justify-center' : '')}>
        <div className="relative">
          {isCollapsed ? (
            <Tip label={userName}>
              <button onClick={() => setProfileOpen(!profileOpen)} className="focus:outline-none cursor-pointer">
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center relative">
                  <span className="text-white text-xs font-bold">{initials}</span>
                  <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-[#031C30] rounded-full" />
                </div>
              </button>
            </Tip>
          ) : (
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className="flex items-center gap-3 group cursor-pointer w-full focus:outline-none"
            >
              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center relative shrink-0">
                <span className="text-white text-xs font-bold">{initials}</span>
                <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-[#031C30] rounded-full" />
              </div>
              <p className="text-[14px] font-normal text-white group-hover:text-blue-400 transition-colors truncate leading-none flex-1 text-left">
                {userName}
              </p>
              <ChevronRight className="text-gray-500 group-hover:text-white transition-colors w-4 h-4 shrink-0" />
            </button>
          )}

          {profileOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setProfileOpen(false)} />
              <div className="absolute bottom-full left-0 mb-1 w-40 p-1 border border-white/10 bg-[#031C30] text-white shadow-lg rounded-sm z-50">
                {onLogout && (
                  <Button
                    variant="tertiary"
                    onClick={() => { setProfileOpen(false); onLogout(); }}
                    className="w-full !text-white hover:!bg-white/5"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </aside>
  );
};
